import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  ClipboardCheck,
  Database,
  FileUp,
  FolderKanban,
  ChartLine,
  Target,
  Trophy,
} from "lucide-react";
import DashboardHeader from "../../components/dashboard/DashboardHeader";
import KpiCard from "../../components/dashboard/KpiCard";
import NoticeBoardCard from "../../components/notices/NoticeBoardCard";
import MyProjectsTable from "../../components/dashboard/MyProjectsTable";
import MyPerformanceCard from "../../components/dashboard/MyPerformanceCard";
import ActivityTrendCard from "../../components/dashboard/ActivityTrendCard";
import RecentLogsCard from "../../components/dashboard/RecentLogsCard";
import { KPI_DEFINITIONS as ACTIVITY_METRICS } from "../../components/project-management/ActivityLogTab";
import { fetchUserDashboard, MAX_LOG_PROJECTS, TREND_DAYS } from "../../controllers/dashboardController";
import { formatRate } from "../performance/performanceUtils";

const QUICK_ACTIONS = [
  { label: "My Projects", icon: FolderKanban, to: "/admin/project-management", primary: true },
  { label: "Records", icon: Database, to: "/admin/database-mangement" },
  { label: "Import Data", icon: FileUp, to: "/admin/import-data" },
  { label: "Performance", icon: ChartLine, to: "/admin/performance-dashboard" },
];

const fmtN = (n) => Number(n || 0).toLocaleString();
const UNAVAILABLE = { value: "—", sub: "Couldn't load" };

const logsFootnote = (activity) => {
  const notes = [];
  if (activity?.skippedProjects) notes.push(`Only your first ${MAX_LOG_PROJECTS} projects are included.`);
  if (activity?.failedProjects) notes.push(`Logs for ${activity.failedProjects} project(s) couldn't be loaded.`);
  return notes.join(" ") || undefined;
};

const buildCards = ({ projects, performance, activity }) => {
  const p = projects?.data;
  const me = performance?.data?.me;
  const a = activity?.data;
  return [
    {
      key: "projects",
      label: "My Active Projects",
      icon: FolderKanban,
      color: "blue",
      to: "/admin/project-management",
      ...(p
        ? {
            value: fmtN(p.active),
            sub: `${fmtN(p.attention.length)} need attention`,
            badge: p.overdue > 0 ? `${fmtN(p.overdue)} overdue` : undefined,
          }
        : UNAVAILABLE),
    },
    {
      key: "recruitment",
      label: "My Recruitment",
      icon: Target,
      color: "green",
      to: "/admin/project-management",
      ...(p
        ? {
            value: p.targets ? `${Math.round((p.recruited / p.targets) * 100)}%` : "—",
            sub: `${fmtN(p.recruited)} of ${fmtN(p.targets)} recruited`,
          }
        : UNAVAILABLE),
    },
    {
      key: "output",
      label: "Screener Responses",
      icon: ClipboardCheck,
      color: "purple",
      to: "/admin/performance-dashboard",
      ...(performance?.data
        ? me
          ? { value: fmtN(me.totalOutput), sub: `${formatRate(me.conversionRate)} conversion` }
          : { value: "0", sub: "No performance data yet" }
        : UNAVAILABLE),
    },
    {
      key: "rank",
      label: "My Rank",
      icon: Trophy,
      color: "dark",
      to: "/admin/performance-dashboard",
      ...(performance?.data
        ? me
          ? { value: `#${me.rank}`, sub: `of ${fmtN(performance.data.rankedCount)}`, badge: me.status }
          : { value: "—", sub: "Not ranked yet" }
        : UNAVAILABLE),
    },
    {
      key: "logged",
      label: "Days Logged",
      icon: CalendarCheck,
      color: "orange",
      ...(a
        ? {
            value: `${a.daysLogged}/${TREND_DAYS}`,
            sub: `last ${TREND_DAYS} days`,
            badge: a.loggedToday ? undefined : "Not logged today",
          }
        : UNAVAILABLE),
    },
  ];
};

const LOADING = { projects: {}, performance: {}, activity: {} };

/** Personal dashboard for the "user" role. */
function UserDashboard({ me }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchUserDashboard(me).then((result) => {
      if (!cancelled) setData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [me]);

  const isLoading = data === null;
  const cards = buildCards(data || LOADING);
  const activity = data?.activity;

  return (
    <div className="w-full max-w-full space-y-6">
      <DashboardHeader
        subtitle="Your projects, performance and activity at a glance."
        actions={QUICK_ACTIONS}
      />

      <section aria-label="Key metrics" className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {cards.map(({ key, to, ...card }) => (
          <KpiCard
            key={key}
            {...card}
            loading={isLoading}
            onClick={to ? () => navigate(to) : undefined}
          />
        ))}
      </section>

      <NoticeBoardCard />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-8">
          <MyProjectsTable
            projects={data?.projects?.data?.projects}
            scopedToUser={data?.projects?.data?.scopedToUser ?? true}
            loading={isLoading}
            error={Boolean(data?.projects?.error)}
          />
        </div>
        <div className="min-w-0 xl:col-span-4">
          <MyPerformanceCard
            data={data?.performance?.data}
            loading={isLoading}
            error={Boolean(data?.performance?.error)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-7">
          <ActivityTrendCard
            title="My Activity"
            description={`Effort actions you logged per day, last ${TREND_DAYS} days.`}
            unit="actions"
            data={activity?.data}
            loading={isLoading}
            error={Boolean(activity?.error)}
            emptyText={`You haven't logged any activity in the last ${TREND_DAYS} days. Log it from a project's Activity tab.`}
            footnote={logsFootnote(activity?.data)}
          />
        </div>
        <div className="min-w-0 xl:col-span-5">
          <RecentLogsCard
            title="My Recent Logs"
            description="Your latest activity entries. Open one to edit it on the project board."
            logs={activity?.data?.recent}
            metrics={ACTIVITY_METRICS}
            loading={isLoading}
            error={Boolean(activity?.error)}
            emptyText={`No activity logged in the last ${TREND_DAYS} days.`}
          />
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
